import React, { useEffect, useCallback, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFeedbacksRequest,
  selectFeedbacksList,
  selectFeedbacksError,
  selectFeedbacksPagination,
} from "../../../dux/feedback";
import { isFetchFeedbacksPending } from "../../../dux/apiStatus";
import styles from "./Settings.module.css";

const PAGE_SIZES = [10, 20, 50];

const FeedbacksTab = React.memo(function FeedbacksTab() {
  const dispatch = useDispatch();
  const list = useSelector(selectFeedbacksList);
  const error = useSelector(selectFeedbacksError);
  const pagination = useSelector(selectFeedbacksPagination);
  const loading = useSelector(isFetchFeedbacksPending);

  const [page, setPage] = useState(pagination.page);
  const [limit, setLimit] = useState(pagination.limit);

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const total = pagination.total ?? 0;

  const fetchFeedbacks = useCallback(
    (opts = {}) => {
      const p = opts.page ?? page;
      const l = opts.limit ?? limit;
      dispatch(fetchFeedbacksRequest({ page: p, limit: l }));
    },
    [dispatch, page, limit]
  );

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    setPage(pagination.page);
    setLimit(pagination.limit);
  }, [pagination.page, pagination.limit]);

  const handlePageChange = useCallback(
    (newPage) => {
      const p = Math.max(1, Math.min(newPage, totalPages));
      setPage(p);
      dispatch(fetchFeedbacksRequest({ page: p, limit }));
    },
    [dispatch, limit, totalPages]
  );

  const handleLimitChange = useCallback(
    (e) => {
      const newLimit = Number(e.target.value) || 20;
      setLimit(newLimit);
      setPage(1);
      dispatch(fetchFeedbacksRequest({ page: 1, limit: newLimit }));
    },
    [dispatch]
  );

  const handleRefresh = useCallback(() => {
    dispatch(fetchFeedbacksRequest({ page, limit }));
  }, [dispatch, page, limit]);

  const rows = Array.isArray(list) ? list : [];
  const isEmpty = rows.length === 0 && !loading;

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    try {
      return new Date(isoString).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return isoString;
    }
  };

  return (
    <Box>
      <Box className={styles.sectionHeader}>
        <Typography variant="h6" className={styles.sectionTitle}>
          User Feedbacks
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per page</InputLabel>
            <Select
              value={limit}
              label="Per page"
              onChange={handleLimitChange}
              disabled={loading}
            >
              {PAGE_SIZES.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            className={styles.primaryButton}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className={styles.alert}
          sx={{ marginBottom: 2 }}
        >
          {error}
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <Box className={styles.centeredContainer}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading feedbacks…
          </Typography>
        </Box>
      ) : isEmpty ? (
        <Box className={styles.emptyStateContainer}>
          <Typography variant="body1" className={styles.emptyStateText}>
            No feedbacks yet.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table className={styles.table} size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={styles.tableHeaderName}>Date</TableCell>
                  <TableCell className={styles.tableHeaderName}>Name</TableCell>
                  <TableCell className={styles.tableHeaderEmail}>
                    Email / Mobile
                  </TableCell>
                  <TableCell className={styles.tableHeaderEmail}>
                    Feedback
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const id = row.id ?? row.created_at + (row.email_address || "");
                  const name = row.name ?? ([row.first_name, row.last_name].filter(Boolean).join(" ").trim() || "—");
                  const contact = [row.email_address, row.mobile_number].filter(Boolean).join(" / ") || "—";
                  const text = row.feedback ?? row.message ?? row.text ?? "—";
                  return (
                    <TableRow key={id}>
                      <TableCell className={styles.tableCellBasic}>
                        {formatDate(row.created_at)}
                      </TableCell>
                      <TableCell className={styles.tableCellBasic}>
                        {name}
                      </TableCell>
                      <TableCell className={styles.tableCellBasic}>
                        {contact}
                      </TableCell>
                      <TableCell className={styles.tableCellBasic} sx={{ maxWidth: 400 }}>
                        <Typography variant="body2" title={text}>
                          {text}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 0 && (
            <Box
              className={styles.paginationContainer}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mt: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Page {pagination.page} of {totalPages}
                {total > 0 && ` · ${total} total`}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  startIcon={<NavigateBeforeIcon />}
                  className={styles.paginationButtonPrevious}
                >
                  Previous
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (pagination.page <= 3) pageNum = i + 1;
                  else if (pagination.page >= totalPages - 2)
                    pageNum = totalPages - 4 + i;
                  else pageNum = pagination.page - 2 + i;
                  return (
                    <Button
                      key={pageNum}
                      size="small"
                      variant={pagination.page === pageNum ? "contained" : "outlined"}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={
                        pagination.page === pageNum
                          ? styles.paginationButtonActive
                          : styles.paginationButton
                      }
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  size="small"
                  disabled={pagination.page >= totalPages || loading}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  endIcon={<NavigateNextIcon />}
                  className={styles.paginationButtonNext}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
});

export default FeedbacksTab;
